/*
 * appZugaenge.ts — Zugangsdaten fuer den Login-Zusatz im Browser-Fenster.
 *
 * Geteilt zwischen dem Electron-Zusatz (AppLoginPanel, meldet per
 * executeJavaScript an) und der Ausfuell-Hilfe im Browser (AppLoginHilfe,
 * kopiert nur in die Zwischenablage), damit beide dieselbe Quelle, denselben
 * Zwischenspeicher und dieselbe Auswahl je Umgebung haben.
 */

export interface CredUser {
  name: string | null;
  email: string;
  password: string | null;
  role: string | null;
  primary: boolean;
  /** false = interactive (human) user, never used by the test suite → safe from
   *  single-active-session logout collisions. Highlighted green in the picker. */
  tester: boolean;
  /** 'production' = real Prod-Bridge account (prod-credentials.json); 'dev' =
   *  test/interactive (dev-Bridge). Picker shows only the set matching the URL. */
  env: 'production' | 'dev';
  /** Nachts gemessen (scripts/konten-daten-messen.py), je Ziel `staging`/`demo`:
   *  wie viel unter diesem Konto liegt — oder warum die Messung scheiterte. null = nie gemessen. */
  daten?: Record<string, KontoDaten> | null;
  /** Persoenliche Staging-Identitaet (dev-Bridge-Konto eines CUI-Nutzers, Rafael 02.11.2026);
   *  `eigen` = die des angemeldeten Nutzers selbst. Server: layouts.ts /app-credentials. */
  identitaet?: boolean;
  eigen?: boolean;
}

export type KontoDaten = { anzahl: number; was: string } | { fehler: string };

/** Hoechste gemessene Anzahl ueber alle Ziele (0, wenn nichts da; null, wenn nie gemessen). */
export function kontoAnzahl(u: Pick<CredUser, 'daten'>): number | null {
  const werte = Object.values(u.daten ?? {}).flatMap(d => 'anzahl' in d ? [d.anzahl] : []);
  return werte.length ? Math.max(...werte) : null;
}

export interface AppCreds {
  name: string;
  productionUrl: string | null;
  /** Staging-Adresse — fuer Partner bei Engelmann bewusst null (Produktions-Datenbank). */
  stagingUrl?: string | null;
  datenGemessenAm?: string | null;
  users: CredUser[];
}

// A live account from the Prod-Bridge directory (GET /api/app-prod-users/:appKey).
// `canLogin` is true only when the CUI holds the plaintext password (account in
// prod-credentials.json or created here); otherwise it's listed for visibility.
export interface ProdUser {
  id: string | null;
  email: string;
  name: string | null;
  role: string | null;
  tenantId: string | null;
  licensed: boolean;
  canLogin: boolean;
  password: string | null;
  /** true = das lokal gemerkte Passwort wich von prod-credentials.json ab; die
   *  SSoT gilt. Sichtbar gemacht, weil sonst nur ein raetselhafter 401 bleibt. */
  pwDrift?: boolean;
}

/**
 * Ein Konto aus Rafaels Freigabeliste im Guide. Bewusst OHNE Passwort: die
 * Liste sagt WER gerade bearbeitet wird, die Zugangsdaten liegen weiter dort,
 * wo sie hingehoeren (credentials.json ueber /api/app-credentials bzw. das
 * Prod-Verzeichnis). Zwei Quellen fuer dasselbe Passwort waeren eine zu viel.
 */
export interface Freigabe {
  kuerzel: string;
  label: string;
  email: string;
  app?: string;
  umgebung: 'staging' | 'produktion';
}

interface AppCredsResponse {
  apps: Record<string, AppCreds>;
  /** Eigene Staging-Identitaet eingetragen, aber nicht aufloesbar (Server-Meldung ohne Wert). */
  identitaetFehler?: string | null;
}

let identitaetFehlerCache: string | null = null;
/** Letzte Server-Meldung zur eigenen Staging-Identitaet; null = keine Stoerung. */
export function identitaetFehler(): string | null { return identitaetFehlerCache; }

// Module-level cache so the credentials are fetched once per CUI session.
let credsCache: Record<string, AppCreds> | null = null;

/** Der bereits geladene Stand, sonst null. */
export function gemerkteAppZugaenge(): Record<string, AppCreds> | null { return credsCache; }

export const defaultKey = (appKey: string) => `app-login-default-${appKey}`;

/**
 * Welche Konten auf dieser Adresse funktionieren: auf einer PRODUKTIONS-Adresse
 * nur der Prod-Satz, sonst (lokal, Partner, Staging) die dev-Konten. Geteilt mit
 * der Ausfuell-Hilfe im Browser (AppLoginHilfe), damit beide dieselbe Auswahl
 * treffen.
 */
export function zugaengeFuerSatz(users: CredUser[], credentialSet: 'production' | 'dev'): CredUser[] {
  const istProdSatz = credentialSet === 'production';
  return users.filter((u) => (istProdSatz ? u.env === 'production' : u.env !== 'production'));
}

/**
 * /api/app-credentials einmal je CUI-Sitzung laden — derselbe Zwischenspeicher
 * wie im Electron-Zusatz. Ein Fehlschlag WIRFT (und vergiftet den Speicher
 * nicht); der Aufrufer zeigt ihn an.
 */
export async function ladeAppZugaenge(): Promise<Record<string, AppCreds>> {
  if (credsCache) return credsCache;
  const r = await fetch('/api/app-credentials', { signal: AbortSignal.timeout(15000) });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const d = (await r.json()) as AppCredsResponse;
  if (!d.apps || typeof d.apps !== 'object') throw new Error('Ungültige Zugangsdaten-Antwort');
  identitaetFehlerCache = d.identitaetFehler ?? null;
  credsCache = d.apps;
  return credsCache;
}

/** Zwischenspeicher verwerfen ("Erneut laden"). */
export function vergissAppZugaenge(): void { credsCache = null; }
